import { useMemo } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import type { DateRange } from '@/contexts/FilterContext';

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  /** Maximum selectable range in days (default 365 — 1 year per spec). */
  maxRangeDays?: number;
  /** How far back data exists (default 3 years per spec). */
  maxHistoryYears?: number;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Date range picker enforcing a max 1-year range within 3 years of history. */
export function DateRangePicker({
  value,
  onChange,
  maxRangeDays = 365,
  maxHistoryYears = 3,
}: DateRangePickerProps) {
  const { minDate, maxDate } = useMemo(() => {
    const now = new Date();
    const min = new Date();
    min.setFullYear(min.getFullYear() - maxHistoryYears);
    return { minDate: isoDate(min), maxDate: isoDate(now) };
  }, [maxHistoryYears]);

  const rangeDays = useMemo(() => {
    const ms = new Date(value.dateTo).getTime() - new Date(value.dateFrom).getTime();
    return Math.round(ms / 86_400_000);
  }, [value]);

  const rangeTooLong = rangeDays > maxRangeDays;

  const handleStart = (dateFrom: string) => {
    onChange({ ...value, dateFrom });
  };
  const handleEnd = (dateTo: string) => {
    onChange({ ...value, dateTo });
  };

  return (
    <div>
      <InputGroup size="sm">
        <InputGroup.Text>
          <i className="bi bi-calendar-range" aria-hidden="true" />
        </InputGroup.Text>
        <Form.Control
          type="date"
          value={value.dateFrom}
          min={minDate}
          max={value.dateTo || maxDate}
          onChange={(e) => handleStart(e.target.value)}
          aria-label="Start date"
        />
        <InputGroup.Text>to</InputGroup.Text>
        <Form.Control
          type="date"
          value={value.dateTo}
          min={value.dateFrom || minDate}
          max={maxDate}
          onChange={(e) => handleEnd(e.target.value)}
          aria-label="End date"
        />
      </InputGroup>
      {rangeTooLong && (
        <small className="text-danger">Date range cannot exceed {maxRangeDays} days.</small>
      )}
    </div>
  );
}
